import type { z } from 'zod/v4';
import type { ClientActionAccess, ClientActionAuthor } from '../definition';

// A client-action hook: () => (payload) => result.
type AnyActionHook = () => (payload: never) => unknown;

type HookSchemaCheck<S extends z.ZodType, Hook extends AnyActionHook> =
  z.output<S> extends Parameters<ReturnType<Hook>>[0]
    ? unknown
    : ['schema payload is not assignable to the hook payload'];

/**
 * Defines a hook-kind lexicon entry, typed by the hook that runs it (passed type-only):
 * `hookAction<typeof useSendEnquiriesAction>()({ ... })`. `type` is the `HookActionTypes`
 * member. The zod schema is checked here against the hook's payload parameter, and the
 * executor map, keyed by the same enum, errors until the hook is added.
 */
export const hookAction =
  <Hook extends AnyActionHook>() =>
  <T extends string, A extends ClientActionAccess, S extends z.ZodType>(definition: {
    type: T;
    description: string;
    access: A;
    authoredBy?: ClientActionAuthor;
    alwaysAllow?: false;
    schema: S & HookSchemaCheck<S, Hook>;
  }): {
    kind: 'hook';
    type: T;
    description: string;
    access: A;
    authoredBy?: ClientActionAuthor;
    alwaysAllow?: false;
    schema: S;
  } => ({ kind: 'hook', ...definition });
